/**
 * Budget React Query Hooks
 *
 * Provides data fetching and mutation hooks for budget-related operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createDetailHooks } from '@/shared/hooks/create-detail-hooks';
import {
  budgetService,
  type BudgetListParams,
  type BudgetProgressParams,
} from '../services/budget-service';
import type { BudgetCreate, BudgetUpdate } from '@/shared/types';

/**
 * Query key factory for budget queries
 */
export const budgetKeys = {
  all: ['budgets'] as const,
  lists: () => [...budgetKeys.all, 'list'] as const,
  list: (params?: BudgetListParams) => [...budgetKeys.lists(), params] as const,
  progress: (params?: BudgetProgressParams) => [...budgetKeys.all, 'progress', params] as const,
  suggestions: () => [...budgetKeys.all, 'suggestions'] as const,
  details: () => [...budgetKeys.all, 'detail'] as const,
  detail: (id: string) => [...budgetKeys.details(), id] as const,
  detailProgress: (id: string, params?: BudgetProgressParams) =>
    [...budgetKeys.detail(id), 'progress', params] as const,
  analytics: (id: string) => [...budgetKeys.detail(id), 'analytics'] as const,
};

/**
 * Hook to fetch paginated list of budgets
 *
 * @param params - Optional filters and pagination
 * @returns Query result with paginated budgets
 */
export function useBudgets(params?: BudgetListParams) {
  return useQuery({
    queryKey: budgetKeys.list(params),
    queryFn: () => budgetService.list(params),
  });
}

/**
 * Hook to fetch budgets with spending progress
 *
 * @param params - Optional date range parameters
 * @returns Query result with budgets including progress
 */
export function useBudgetsWithProgress(params?: BudgetProgressParams) {
  return useQuery({
    queryKey: budgetKeys.progress(params),
    queryFn: () => budgetService.listWithProgress(params),
  });
}

/**
 * Hook to fetch budget suggestions (unbudgeted categories with high spending)
 */
export function useBudgetSuggestions() {
  return useQuery({
    queryKey: budgetKeys.suggestions(),
    queryFn: () => budgetService.suggestions(),
  });
}

/**
 * useBudget(id?) — conditional fetch, data may be undefined.
 * useBudgetSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useBudget, useBudgetSuspense] = createDetailHooks(
  budgetKeys.detail,
  budgetService.getById,
);

/**
 * useBudgetProgress(id?) — conditional fetch, data may be undefined.
 * useBudgetProgressSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 *
 * Fetches current-period progress for a single budget (server defaults to current period).
 */
export const [useBudgetProgress, useBudgetProgressSuspense] = createDetailHooks(
  (id: string) => budgetKeys.detailProgress(id),
  (id: string) => budgetService.getProgress(id),
);

/**
 * useBudgetAnalytics(id?) — conditional fetch, data may be undefined.
 * useBudgetAnalyticsSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useBudgetAnalytics, useBudgetAnalyticsSuspense] = createDetailHooks(
  budgetKeys.analytics,
  budgetService.getAnalytics,
);

/**
 * Hook to create a new budget
 *
 * @returns Mutation for creating budgets
 */
export function useCreateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BudgetCreate) => budgetService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to update a budget
 *
 * @returns Mutation for updating budgets
 */
export function useUpdateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BudgetUpdate }) =>
      budgetService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to delete a budget
 *
 * @returns Mutation for deleting budgets
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => budgetService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
